import {useEffect,useRef,useState} from 'react';
import {compassHeading,currentHeading,smoothHeading,travelHeading,type HeadingSample,type OrientationReading} from './compassHeading';

const screenAngle=()=>window.screen?.orientation?.angle ?? (window as unknown as {orientation?:number}).orientation ?? 0;

/** Local map direction only: sensor events never publish additional location messages. */
export function useCompassHeading(enabled:boolean,position:GeolocationPosition|null){
  const [heading,setHeading]=useState<HeadingSample|null>(null);
  const compass=useRef<HeadingSample|null>(null),course=useRef<HeadingSample|null>(null);
  const filtered=useRef<HeadingSample|null>(null);
  useEffect(()=>{
    compass.current=null;course.current=null;filtered.current=null;setHeading(null);
    if(!enabled)return;
    let visible=document.visibilityState!=='hidden';
    let invalidSince:number|null=null;
    const publish=()=>{
      if(invalidSince!==null&&Date.now()-invalidSince>=2000){compass.current=null;filtered.current=null;}
      const next=visible?currentHeading(compass.current,course.current,Date.now()):null;
      // Finish smoothing even when the device stops emitting after a turn.
      filtered.current=next?.source==='compass'?smoothHeading(filtered.current,next.degrees,Date.now()):null;
      const display=filtered.current??next;
      const rounded=display?{...display,degrees:Math.round(display.degrees)%360}:null;
      setHeading(old=>old?.degrees===rounded?.degrees&&old?.source===rounded?.source?old:rounded);
    };
    const orientation=(event:Event)=>{
      if(!visible)return;
      const degrees=compassHeading(event as unknown as OrientationReading,screenAngle());
      // Ignore unrelated relative events when the browser also delivers absolute ones.
      const reading=event as unknown as OrientationReading;
      if(degrees===null){if(reading.absolute||typeof reading.webkitCompassHeading==='number')invalidSince??=Date.now();return;}
      invalidSince=null;
      compass.current={degrees,timestamp:Date.now(),source:'compass'};
    };
    const clear=()=>{compass.current=null;course.current=null;filtered.current=null;invalidSince=null;setHeading(null);};
    const visibility=()=>{visible=document.visibilityState!=='hidden';clear();};
    const hide=()=>{visible=false;clear();},show=()=>{visible=document.visibilityState!=='hidden';clear();};
    // Consume readings only when the browser supplies them without prompting.
    // Never request motion permission on load, map taps, or GPS restarts.
    // Permission-gated browsers fall back to validated GPS travel direction.
    window.addEventListener('deviceorientation',orientation);
    window.addEventListener('deviceorientationabsolute',orientation);
    window.addEventListener('orientationchange',clear);
    window.screen?.orientation?.addEventListener('change',clear);
    document.addEventListener('visibilitychange',visibility);
    window.addEventListener('pagehide',hide);window.addEventListener('pageshow',show);
    const timer=setInterval(publish,100);
    return()=>{
      clearInterval(timer);
      window.removeEventListener('deviceorientation',orientation);
      window.removeEventListener('deviceorientationabsolute',orientation);
      window.removeEventListener('orientationchange',clear);
      window.screen?.orientation?.removeEventListener('change',clear);
      document.removeEventListener('visibilitychange',visibility);
      window.removeEventListener('pagehide',hide);window.removeEventListener('pageshow',show);
    };
  },[enabled]);
  useEffect(()=>{
    if(!enabled||document.visibilityState==='hidden')return;
    const next=travelHeading(position,Date.now());
    if(next)course.current=next;
    else if(position?.coords.speed!=null&&Number.isFinite(position.coords.speed)&&position.coords.speed<0.8)course.current=null;
  },[enabled,position]);
  return {heading:enabled?heading:null};
}
